import { useEffect, useRef, useState } from 'react'
import type { Diamond } from '../api/types'
import { useStoneImage } from '../lib/representativeImages'
import { resolvesToFrames, withProxyWidth } from '../lib/media'
import { viewer360Api } from '../api/endpoints'

/**
 * Fills a `relative` container with a stone's image. Chain (Tej 2026-08-03,
 * "no 3d silhouette — back-up image of that shape from what we already
 * have, watermarked"):
 *
 *   1. The stone's OWN photo.
 *   2. Its 360° viewer's middle frame (real footage of THIS stone) when it
 *      has video but no photo — resolved through the /api/v proxy.
 *   3. A REPRESENTATIVE catalog photo — same shape, closest colour family,
 *      falling to the bundled per-shape studio photo (can't 404) — with a
 *      WATERMARK: "image not available — contact supplier". The watermark
 *      is what keeps a real-but-substitute photo honest (Vikram's
 *      2026-07-26 objection), so it must never be omitted on stage 3.
 *
 * There is NO silhouette stage — the chain always ends on a real photo.
 * The watermark hides on tiny thumbnails (under 72px tall) where it would
 * be unreadable noise; those contexts open into surfaces that do show it.
 */

// videoUrl → poster frame URL (null = viewer doesn't resolve to frames).
const posterCache = new Map<string, string | null>()
const posterInflight = new Map<string, Promise<string | null>>()

function resolvePoster(videoUrl: string): Promise<string | null> {
  const cached = posterCache.get(videoUrl)
  if (cached !== undefined) return Promise.resolve(cached)
  let p = posterInflight.get(videoUrl)
  if (!p) {
    p = viewer360Api
      .frames(videoUrl)
      .then((r) => (r.resolved && r.frames.length ? r.frames[Math.floor(r.frames.length / 2)] : null))
      .catch(() => null)
      .then((v) => {
        posterCache.set(videoUrl, v)
        posterInflight.delete(videoUrl)
        return v
      })
    posterInflight.set(videoUrl, p)
  }
  return p
}

export function StoneImage({
  stone,
  imgClassName = 'absolute inset-0 h-full w-full object-cover',
  width,
  onSubstitute,
}: {
  stone: Pick<Diamond, 'shape' | 'color' | 'media'> & { stockId?: string }
  /** @deprecated kept so existing call sites compile. */
  badge?: boolean
  /** @deprecated kept so existing call sites compile. */
  silhouetteLabel?: boolean
  imgClassName?: string
  /** Proxy-resize hint (CSS px of the box, honouring the server's
   *  96/256/512/1024 ladder). Appended as `&w=` to /api/v photo/img URLs
   *  ONLY (withProxyWidth) — a 40px thumb should not download a 2MB still.
   *  External supplier URLs and bundled assets are never touched. */
  width?: number
  /** Fires once when the render lands on the WATERMARKED stand-in (stage 3)
   *  — i.e. this stone could not show its own media. Curated rails (landing
   *  featured) use it to demote the stone; browse grids ignore it. */
  onSubstitute?: () => void
}) {
  const videoUrl = stone?.media?.videoUrl ?? null
  const { src, isSubstitute, onLoad, onError } = useStoneImage(stone)

  // Stage 2 — real 360 frame of THIS stone, preferred over any substitute.
  // TRI-STATE so we never flash the "image not available" watermark while the
  // frame is still resolving (Tej 2026-09-11 — the modal/card flicker: a
  // borrowed photo + watermark showed for the network round-trip, then the real
  // frame replaced it). `undefined` = still resolving · `null` = resolved, no
  // frame · string = the frame URL. Seeded synchronously from the cache so an
  // already-known stone never flashes at all.
  const canHavePoster = isSubstitute && !!videoUrl && resolvesToFrames(videoUrl)
  const [poster, setPoster] = useState<string | null | undefined>(
    () => (canHavePoster ? posterCache.get(videoUrl as string) : null),
  )
  useEffect(() => {
    if (!canHavePoster) { setPoster(null); return }
    const cached = posterCache.get(videoUrl as string)
    if (cached !== undefined) { setPoster(cached); return }   // already resolved
    setPoster(undefined)                                       // → pending
    let cancelled = false
    resolvePoster(videoUrl as string).then((v) => { if (!cancelled) setPoster(v) })
    return () => { cancelled = true }
  }, [canHavePoster, videoUrl])

  // Watermark legibility gate — skip it on tiny thumbs (table rows). boxTall
  // ONLY gates the "image not available" watermark + its blur, which only ever
  // show on a SUBSTITUTE. So we create the ResizeObserver only when this is a
  // substitute — a real photo (the ~95% case) skips it entirely. This matters
  // at scale: a 48-card grid or 50-row table was spinning up ~48–50 observers,
  // and tearing them all down + up on a Cards⇄Table toggle was a big chunk of
  // the toggle's main-thread freeze. boxTall defaults true (unused when not a
  // substitute); the effect re-runs if a real photo 404s into a substitute.
  const boxRef = useRef<HTMLDivElement | null>(null)
  const [boxTall, setBoxTall] = useState(true)
  useEffect(() => {
    if (!isSubstitute) return
    const el = boxRef.current?.parentElement
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height ?? 0
      setBoxTall(h >= 72)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [isSubstitute])

  const posterPending = canHavePoster && poster === undefined
  const showPoster = !!poster   // a resolved frame URL (never '' or null)
  const finalSrc = withProxyWidth(showPoster ? (poster as string) : src, width)

  // Every image fills its box with object-cover — a wall of uniform, centred
  // squares (the system doctrine, stated in MediaModal: stones are centred in
  // their frames so cover's mild crop never touches the subject). A 360 POSTER
  // still is framed a shade tighter than a studio photo, so cover crops it a
  // little more — but uniform + always-centred beats the borrowed still
  // floating inside object-contain letterbox bands (Tej 2026-10-22, reverting
  // the 2026-10-21 object-contain experiment, which read as "not centred").
  // Watermark ONLY once we are CERTAIN there is no real image: it's a
  // substitute, we are NOT still resolving a 360 frame, and none was found.
  const watermarked = isSubstitute && !posterPending && !showPoster

  // Crossfade — the <img> stays transparent until the CURRENT src has actually
  // loaded, then fades in over 150ms. A late fallback swap (own photo 404 →
  // representative) fades in instead of popping, which was the visible
  // thumbnail flicker on /search. Pure CSS (a keyframe, not a transition, so
  // it can never fight a call site's transition-property — DiamondCard
  // transitions `transform` on this same <img>). Fallback-chain semantics and
  // the watermark are untouched.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const imgLoaded = loadedSrc === finalSrc
  // Clean loading state (Tej 2026-09-11): show a shimmer — never a bare box or a
  // premature "not available" watermark — while the 360 frame is still
  // resolving OR the chosen image hasn't painted. finalSrc changing (e.g.
  // representative → resolved frame) resets imgLoaded, so the shimmer also
  // bridges that swap instead of popping.
  const loading = posterPending || !imgLoaded

  const notifiedRef = useRef(false)
  useEffect(() => {
    if (watermarked && !notifiedRef.current) {
      notifiedRef.current = true
      onSubstitute?.()
    }
  }, [watermarked, onSubstitute])
  // A borrowed photo is a REAL diamond of the right shape but NOT this stone —
  // blur it so it reads unmistakably as a stand-in, never the actual stone
  // (Tej 2026-08-17). Gated to boxTall: tiny table thumbs would turn to mush
  // and they carry no watermark anyway. `scale-110` overscans so the blur's
  // soft edge is clipped by the media box instead of showing a bare rim.
  const blurred = watermarked && boxTall

  return (
    <>
      {/* Soft base so the tile is never a hard empty rect while an image decodes. */}
      <div ref={boxRef} className="absolute inset-0 bg-gradient-to-br from-[#faf7f1] to-[#eef1f6]" aria-hidden />
      {/* Loading shimmer — sits above the base, below the <img> (which fades in
          on top once painted). Reduced-motion freezes it (see .skeleton). */}
      {loading && (
        <div className="skeleton absolute inset-0" aria-hidden data-testid="stone-image-loading" />
      )}
      <img
        src={finalSrc}
        alt={stone.shape || ''}
        loading="lazy"
        data-testid="stone-silhouette"
        data-substitute={watermarked ? 'true' : undefined}
        onLoad={() => {
          setLoadedSrc(finalSrc)
          if (!showPoster) onLoad()
        }}
        onError={showPoster
          ? () => { posterCache.set(videoUrl!, null); setPoster(null) }
          : onError}
        className={`${imgClassName}${blurred ? ' scale-110 blur-[5px] brightness-[0.82]' : ''}${imgLoaded ? ' stone-img-loaded' : ' opacity-0'}`}
      />
      {watermarked && imgLoaded && boxTall && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-2" data-testid="stone-watermark">
          <span className="rounded-md bg-navy/70 px-3 py-1.5 text-center text-[9px] font-bold uppercase leading-snug tracking-[0.16em] text-white/95 shadow-elev-1 backdrop-blur-[2px]">
            Image not available
            <br />
            contact supplier
          </span>
        </div>
      )}
    </>
  )
}
